import { LANGUAGES, type Language, type TranslatorState } from '@voxa/shared';
import { TranslatorError, failureFromApi, type TranslatorFailure } from './failures.ts';
import type { CallParts } from './parts.ts';
import { PlayoutTracker, type PlayoutSample } from './playout.ts';
import type { ProviderEventRecon } from './recon.ts';
import type {
  Translator,
  TranslatorCapabilities,
  TranslatorCredentials,
  TranslatorHandlers,
  TranslatorMetrics,
} from './types.ts';

/**
 * OpenAI Realtime Translation over WebRTC (section 6.1).
 *
 * The shape of the thing: one RTCPeerConnection per listening direction. We
 * push the peer's *original* track into it and get translated audio back. The
 * session runs for the whole call — a translation session processes
 * continuously, so there is no `response.create` and no turn-taking here. That
 * is the difference between a translator and a voice agent, and confusing the
 * two is the fastest way to a conversation that answers back.
 *
 * Deliberately absent: any local VAD gating of the input. Pauses are part of
 * the signal the model needs, so the whole stream goes through.
 */

const CAPABILITIES: TranslatorCapabilities = {
  provider: 'openai',
  transport: 'webrtc',
  subtitles: true,
  // Whether the *source* language is written down too is the server's
  // decision, not this file's: it is the server that asks the provider for it
  // when minting the session. Constant `true` here was a promise nobody kept —
  // the provider does it only on request, we never requested it, and not one
  // `session.input_transcript.delta` ever arrived. See task 11.
  sourceSubtitles: false,
  // The translation session takes the output language only; the model infers
  // the source. Claiming otherwise would be inventing a field.
  sourceLanguageHint: false,
  /**
   * Everything the room can be opened in — the app's own list, not a second
   * copy of it, because the adapter passes the code straight through and the
   * provider accepts every one of them.
   *
   * The provider's accepted set is not in its documentation (which, as of
   * 2026-09, names 13 output languages and is out of date). It is in the API:
   * POST a session with a nonsense output language and the 400 names all 58 it
   * will take. That is the check to repeat before adding a language here.
   */
  languages: LANGUAGES,
  maxSessionSeconds: null,
};

/** Provider events we act on. Everything else is ignored, not an error. */
interface ProviderEvent {
  type: string;
  delta?: string;
  /**
   * The provider's own clock, milliseconds since this session opened. Present
   * on every transcript delta of both kinds — which is what lets task 2 time a
   * part without ever subtracting two of this page's timestamps.
   */
  elapsed_ms?: number;
  error?: { message?: string; type?: string };
}

export interface OpenAITranslatorOptions {
  /**
   * Off unless a recon run is switched on (task 1). The adapter only feeds it
   * what already arrived; what may be remembered about an event, and what may
   * never leave the browser, is decided in `recon.ts`.
   */
  recon?: ProviderEventRecon | null;
  /**
   * Divides the call into parts and times them (task 2). Fed the same events
   * the subtitles come from, so a part and the words in it cannot disagree.
   */
  parts?: CallParts | null;
  /**
   * What the server said at `/api/capabilities`. Passed in rather than assumed
   * so the advertised capability and the session that was actually minted
   * cannot disagree.
   */
  sourceSubtitles?: boolean;
}

export class OpenAITranslator implements Translator {
  readonly capabilities: TranslatorCapabilities;

  #handlers: TranslatorHandlers;
  #recon: ProviderEventRecon | null;
  #parts: CallParts | null;
  #pc: RTCPeerConnection | null = null;
  #events: RTCDataChannel | null = null;
  #audio: MediaStream | null = null;
  #state: TranslatorState = 'idle';
  #sessionId = '';
  #sequence = 0;
  #closed = false;

  #connectStartedAt: number | null = null;
  /** Set once the session stops, so the final duration stops moving. */
  #sessionSeconds: number | null = null;
  #firstInputAt: number | null = null;
  #lastInputAt: number | null = null;
  #lastOutputAt: number | null = null;
  #statsTimer: ReturnType<typeof setInterval> | null = null;

  #playout = new PlayoutTracker();

  #metrics: TranslatorMetrics = {
    connectMs: null,
    playoutDelayMs: null,
    audioPlayedMs: null,
    acceleratedMs: null,
    stretchedMs: null,
    firstTranslationMs: null,
    estimatedLagMs: null,
    rttMs: null,
    jitterMs: null,
    packetsLost: null,
  };

  constructor(handlers: TranslatorHandlers, options: OpenAITranslatorOptions = {}) {
    this.#handlers = handlers;
    this.#recon = options.recon ?? null;
    this.#parts = options.parts ?? null;
    this.capabilities = { ...CAPABILITIES, sourceSubtitles: options.sourceSubtitles ?? false };
  }

  get state(): TranslatorState {
    return this.#state;
  }

  get sessionId(): string {
    return this.#sessionId;
  }

  /**
   * How long this session has been open, in seconds — the number to hold
   * against the provider's invoice. Counted from the first byte of the
   * handshake, so it rounds up rather than down, and frozen once the session
   * stops. Not the provider's own accounting, which we cannot see from here.
   */
  get sessionSeconds(): number {
    if (this.#sessionSeconds !== null) return this.#sessionSeconds;
    if (this.#connectStartedAt === null) return 0;
    return Math.round((performance.now() - this.#connectStartedAt) / 1000);
  }

  get metrics(): TranslatorMetrics {
    return { ...this.#metrics };
  }

  async connect(
    inputTrack: MediaStreamTrack,
    _targetLanguage: Language,
    credentials: TranslatorCredentials,
  ): Promise<void> {
    if (this.#closed) throw new Error('translator is closed');
    this.#sessionId = credentials.sessionId;
    this.#connectStartedAt = performance.now();
    this.#setState('connecting');

    const pc = new RTCPeerConnection();
    this.#pc = pc;

    // The one line that defines this whole architecture: the *remote* track
    // goes to the provider. Never this browser's microphone, never a mix of
    // the page, never already-translated audio — any of those is a feedback
    // loop or a self-translation.
    pc.addTrack(inputTrack, new MediaStream([inputTrack]));

    pc.addEventListener('track', (event) => {
      const stream = event.streams[0] ?? new MediaStream([event.track]);
      this.#audio = stream;
      this.#handlers.onTranslatedStream(stream);
    });

    pc.addEventListener('connectionstatechange', () => {
      if (this.#closed) return;
      switch (pc.connectionState) {
        case 'connected':
          this.#markReady();
          break;
        case 'disconnected':
          this.#setState('reconnecting');
          break;
        case 'failed':
          this.#stop('failed', { code: 'offline' });
          break;
        case 'closed':
          this.close();
          break;
        default:
          break;
      }
    });

    const events = pc.createDataChannel('oai-events');
    this.#events = events;
    events.addEventListener('message', (event) => this.#onProviderEvent(event));
    events.addEventListener('open', () => this.#markReady());

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // Our own server, not the provider. It holds the ephemeral credential and
    // forwards this offer, because the provider's API is refused outright for
    // some regions at its CDN — from those networks a direct call here comes
    // back 403 with no CORS headers, which a browser can only report as a bare
    // network failure. The media is unaffected: it is a WebRTC connection this
    // page opens to the provider, and it does not go through our server.
    let res: Response;
    try {
      res = await fetch(credentials.sdpUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/sdp' },
        body: offer.sdp ?? '',
      });
    } catch {
      throw this.#fail({ code: 'offline' });
    }

    if (!res.ok) {
      throw this.#fail(await readFailure(res));
    }

    await pc.setRemoteDescription({ type: 'answer', sdp: await res.text() });
    this.#startStatsPolling();
  }

  close(): void {
    this.#stop('closed');
  }

  /**
   * The single exit. Frees everything this adapter owns and lands on a
   * terminal state: `closed` when the call let the session go, `failed` when
   * the session died on its own.
   *
   * Failing releases just as hard as closing, and that is the point. The
   * provider bills a session for as long as it is open, working or not, and
   * this browser holds the only end of it that can hang up — the audio path
   * goes straight from here to the provider, so nothing on our server can
   * close it. A session left running behind a "translator stopped" notice
   * costs exactly what a working one costs.
   */
  #stop(state: 'closed' | 'failed', failure?: TranslatorFailure): void {
    if (this.#closed) return;
    this.#closed = true;

    const at = performance.now();
    if (this.#connectStartedAt !== null) {
      this.#sessionSeconds = Math.round((at - this.#connectStartedAt) / 1000);
    }

    // The last recon summary, sent from the one place every exit goes through.
    // It has to happen here rather than in the caller: a session that failed
    // on its own is exactly the one whose event log is worth reading, and by
    // the time the state change is delivered the reason is all that is left.
    this.#recon?.finish(this.#sessionMs(at));
    // Same argument for the part that was still open: a call that ended
    // mid-sentence has one, and dropping it would quietly bias every average
    // towards the parts that finished tidily.
    this.#parts?.finish();

    if (this.#statsTimer) clearInterval(this.#statsTimer);
    this.#statsTimer = null;

    try {
      this.#events?.close();
    } catch {
      /* already gone */
    }
    // Only the tracks this adapter owns — the translated audio coming back.
    // The input track belongs to the call and must keep flowing.
    for (const track of this.#audio?.getTracks() ?? []) track.stop();
    try {
      this.#pc?.close();
    } catch {
      /* already gone */
    }
    this.#events = null;
    this.#pc = null;
    this.#audio = null;

    // Last, so the teardown's own events land on an adapter that has already
    // stopped listening and cannot overwrite the reason with 'closed'.
    this.#setState(state, failure);
  }

  /** Milliseconds into this session, which is the timeline a recon run reads. */
  #sessionMs(at: number): number {
    return this.#connectStartedAt === null ? 0 : at - this.#connectStartedAt;
  }

  #markReady(): void {
    if (this.#state === 'ready' || this.#closed) return;
    if (this.#pc?.connectionState !== 'connected') return;
    if (this.#events?.readyState !== 'open') return;

    if (this.#connectStartedAt !== null) {
      this.#metrics.connectMs = Math.round(performance.now() - this.#connectStartedAt);
    }
    this.#setState('ready');
    this.#emitMetrics();
  }

  #onProviderEvent(event: MessageEvent): void {
    if (this.#closed || typeof event.data !== 'string') return;

    const now = performance.now();
    let parsed: ProviderEvent;
    try {
      parsed = JSON.parse(event.data) as ProviderEvent;
    } catch {
      // Counted rather than dropped when a recon run is on: an event this
      // adapter cannot even parse is the most interesting kind there is.
      this.#recon?.observe(null, this.#sessionMs(now), event.data.length);
      return;
    }

    // Before the switch, so the recon sees the events this adapter ignores —
    // which is the entire question task 1 exists to answer. It costs a walk
    // over an object that was just parsed anyway, and only when switched on.
    this.#recon?.observe(parsed, this.#sessionMs(now), event.data.length);

    switch (parsed.type) {
      case 'session.input_transcript.delta': {
        if (typeof parsed.delta !== 'string') return;
        this.#firstInputAt ??= now;
        this.#lastInputAt = now;
        this.#parts?.observe('input', parsed.elapsed_ms ?? null);
        this.#emitChunk('input', parsed.delta, now);
        this.#updateLag();
        break;
      }

      case 'session.output_transcript.delta': {
        if (typeof parsed.delta !== 'string') return;
        this.#lastOutputAt = now;
        if (this.#metrics.firstTranslationMs === null && this.#firstInputAt !== null) {
          this.#metrics.firstTranslationMs = Math.round(now - this.#firstInputAt);
        }
        this.#parts?.observe('output', parsed.elapsed_ms ?? null);
        this.#emitChunk('output', parsed.delta, now);
        this.#updateLag();
        break;
      }

      case 'error': {
        // Not fatal to the *call* — the UI offers an explicit restart, which
        // builds a fresh adapter. This session, though, is over: keeping it
        // open would bill for a translator the screen has already given up on.
        this.#stop('failed', { code: 'unknown' });
        break;
      }

      default:
        break;
    }
  }

  #emitChunk(direction: 'input' | 'output', text: string, at: number): void {
    this.#sequence += 1;
    this.#handlers.onTranscript({
      direction,
      sequence: this.#sequence,
      text,
      at,
      // The delta stream carries no completion marker, so nothing here may
      // claim a segment is final.
      final: false,
    });
  }

  /**
   * How far behind the translation is running, taken from the gap between the
   * two transcript streams. Honest about what it is: an estimate good enough
   * to raise "the translation is lagging", not the measured semantic lag of
   * section 10.
   */
  #updateLag(): void {
    if (this.#lastInputAt === null) return;
    if (this.#lastOutputAt === null) {
      this.#metrics.estimatedLagMs = Math.round(performance.now() - this.#lastInputAt);
    } else {
      this.#metrics.estimatedLagMs = Math.max(
        0,
        Math.round(this.#lastInputAt - this.#lastOutputAt),
      );
    }
    this.#emitMetrics();
  }

  #startStatsPolling(): void {
    this.#statsTimer = setInterval(() => {
      void this.#pollStats();
    }, 3000);
  }

  async #pollStats(): Promise<void> {
    const pc = this.#pc;
    if (!pc || this.#closed) return;
    const report = await pc.getStats();
    report.forEach((stat) => {
      if (stat.type === 'candidate-pair' && (stat as RTCIceCandidatePairStats).nominated) {
        const pair = stat as RTCIceCandidatePairStats;
        this.#metrics.rttMs =
          pair.currentRoundTripTime !== undefined
            ? Math.round(pair.currentRoundTripTime * 1000)
            : null;
      }
      if (stat.type === 'inbound-rtp' && (stat as RTCInboundRtpStreamStats).kind === 'audio') {
        const rtp = stat as RTCInboundRtpStreamStats;
        this.#metrics.jitterMs = rtp.jitter !== undefined ? Math.round(rtp.jitter * 1000) : null;
        this.#metrics.packetsLost = rtp.packetsLost ?? null;
        // The playback side. These fields are not in every browser's typings
        // and not in every implementation either, so they are read loosely and
        // each one that is missing simply stays null.
        this.#playout.observeStats(rtp as PlayoutSample);
        Object.assign(this.#metrics, this.#playout.metrics);
      }
    });
    this.#emitMetrics();
  }

  #emitMetrics(): void {
    this.#handlers.onMetrics({ ...this.#metrics });
  }

  /** Releases the session, records the reason, hands back the error to throw. */
  #fail(failure: TranslatorFailure): TranslatorError {
    this.#stop('failed', failure);
    return new TranslatorError(failure);
  }

  #setState(state: TranslatorState, failure?: TranslatorFailure): void {
    if (this.#state === state) return;
    this.#state = state;
    this.#handlers.onState(state, failure);
  }
}

/** Reads our server's error body for a code; the status is the fallback. */
async function readFailure(res: Response): Promise<TranslatorFailure> {
  let code = '';
  try {
    const body = (await res.json()) as { error?: { code?: string } };
    code = body.error?.code ?? '';
  } catch {
    // A body that is not our JSON tells us nothing; the status still does.
  }
  return failureFromApi(code, res.status);
}
